import { capitalize } from 'lodash'
import { makeAutoObservable, runInAction } from 'mobx'
import classNames from 'classnames'
import type { IAlert } from 'shared/ui/Alert/types'
import { Button } from 'shared/ui/AlertBanner/Button/Button'
import { EnumAlertBannerVariant } from 'shared/ui'
import { type MergeField } from 'entities/MergeField/model/MergeField'
import { IntegrationKey } from 'entities/Integrations'
import { ChatbotBookingType } from 'entities/Chatbot'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { MergeFieldsIconAction } from 'widgets/MergeField/ui/actions/MergeFieldsIconAction/MergeFieldsIconAction'
import { type StepObjectiveStore } from 'pages/chatbot/ui/ChatbotEdit/StepObjective'
import styles from './styles.module.scss'

export class StepObjectiveBookingStore {
  private _requiredFields: string[] = []
  private _optionsFields: string[] = []
  private _loading = false
  private _errorAlertType: 'alert' | undefined = undefined

  constructor(private _stepObjectiveStore: StepObjectiveStore, private _entities: IEntitiesStore) {
    makeAutoObservable(this)
  }

  get messageFieldStore() {
    return this._stepObjectiveStore.messageFieldStore
  }

  get chatbotStore() {
    return this._stepObjectiveStore.chatbotStore
  }

  get chatbot() {
    return this._stepObjectiveStore.chatbotStore.model
  }

  get loading() {
    return this._loading
  }

  get connectCalendarStore() {
    return this._stepObjectiveStore.chatbotStore.agentBookAppointmentStepsStore.connectCalendarStore
  }

  get bookingFieldsStore() {
    return this._stepObjectiveStore.bookingFieldsStore
  }

  get errorAlert(): IAlert | undefined {
    const bookingType = this.connectCalendarStore.bookingType
    if (bookingType !== ChatbotBookingType.TimeChat) return undefined

    if (this._notUseRequiredFields.length) {
      const integrationName = this.connectCalendarStore.isCalendlySelected ? 'Calendly' : 'HubSpot'
      const type = this._errorAlertType || 'warning'
      return {
        type,
        title: `${integrationName} requires additional fields.`,
        desc: (
          <>
            {`Add the missing fields or click reset to sync with ${integrationName}.`}
            <br />
            <br />
            <ul className={styles.list}>
              {this._notUseRequiredFields.map((item) => {
                return (
                  <li key={item.name} className={classNames(styles[`state${type}`])}>
                    {capitalize(item.integrationKey)}: {item.label}
                  </li>
                )
              })}
            </ul>
            <div className={styles.actions}>
              <Button
                variantBanner={
                  type === 'alert' ? EnumAlertBannerVariant.Error : EnumAlertBannerVariant.Warning
                }
                variant={'primary'}
                text={'Reset fields'}
                onClick={this._setDefaultInstructions}
              />
            </div>
          </>
        ),
      }
    }
  }

  dispose = () => {
    this._errorAlertType = undefined
    this._requiredFields = []
    this._optionsFields = []
    this._entities.mergeFieldStore.removeMergeFieldsIntegrations(IntegrationKey.calendly)
    this._entities.mergeFieldStore.removeDynamicMergeFields(IntegrationKey.hubspot)
  }

  init = async () => {
    const integrationKey = this.connectCalendarStore.selectedIntegrationKey
    const bookAppointmentLinkId = this.connectCalendarStore.eventLink?.data?.id
    const integrations =
      integrationKey === IntegrationKey.calendly
        ? [IntegrationKey.hubspot, IntegrationKey.calendly]
        : [IntegrationKey.hubspot]

    if (integrationKey) {
      this._loading = true
    }

    this.messageFieldStore.setActions([
      {
        iconButtonComponent: (
          <MergeFieldsIconAction
            onAddMergeField={(value) => this._stepObjectiveStore.onChooseMergeField(value)}
            isAllIntegration
            includeIntegrations={integrations}
            excludeFieldsIntegrations={{
              [IntegrationKey.salesmessage]: ['number'],
            }}
          />
        ),
      },
    ])

    if (integrationKey && bookAppointmentLinkId) {
      await this.bookingFieldsStore.fetchFields({
        params: { id: bookAppointmentLinkId, integrationKey },
      })
    }

    this._initBookingFields()
    this._initProperties()
  }

  private get _notUseRequiredFields() {
    const messageFormFields = Array.from(this._stepObjectiveStore.existMergeFields.values()).filter(
      (item) => this._requiredFields.includes(item.key)
    )
    const requiredFields = this.bookingFieldsStore.allMergeFields.filter((item) =>
      this._requiredFields.includes(item.name)
    )
    return requiredFields.reduce<MergeField[]>((state, item) => {
      const exist = messageFormFields.find((field) => field.key === item.name)

      if (!exist) state.push(item)

      return state
    }, [])
  }

  validateRequiredFields = () => {
    const bookingType = this.connectCalendarStore.bookingType

    this._errorAlertType = undefined

    if (bookingType !== ChatbotBookingType.TimeChat) return false

    if (this._notUseRequiredFields.length) {
      this._errorAlertType = 'alert'

      return true
    }

    this._errorAlertType = undefined

    return false
  }

  private _initBookingFields = () => {
    const integrationKey = this.connectCalendarStore.selectedIntegrationKey
    if (!integrationKey) return
    this._entities.mergeFieldStore.addMergeFieldsIntegrations(
      IntegrationKey[integrationKey],
      this.bookingFieldsStore.hubspotMergeFields
    )
  }

  private _formatedFieldText = (value: string[]) => {
    return value.reduce<string>((state, item) => {
      const template = `{ ${item} }`

      state += `* ${template} \n`

      return state
    }, '')
  }

  private _setDefaultInstructions = () => {
    const preselectText = `Please collect the following information from the contacts: \n\n${this._formatedFieldText(
      this._requiredFields
    )}${this._formatedFieldText(this._optionsFields)}`
    this._stepObjectiveStore.setMessageField(preselectText)
  }

  private _initProperties = () => {
    const value = this.bookingFieldsStore.selectedProperties

    if (!value.length) {
      this._loading = false

      return
    }

    runInAction(() => {
      this._requiredFields = []
      this._optionsFields = []
    })

    value.forEach((item) => {
      if (!item.customField) return

      if (item.required) this._requiredFields.push(item.customField.key)
      if (!item.required) this._optionsFields.push(item.customField.key)
    })

    const isShareLink = this.connectCalendarStore.bookingType === ChatbotBookingType.ShareLink

    if (!this.chatbotStore.id) {
      if (!isShareLink) {
        this._setDefaultInstructions()
      } else {
        this._stepObjectiveStore.setMessageField('')
      }
    } else {
      const { originalValues, setDataCollectionInstruction } = this.chatbot.settings
      const bookAppointmentLinkOriginId = originalValues.book_appointment_link?.id
      const bookAppointmentLinkId = this.connectCalendarStore.eventLink?.data?.id

      if (bookAppointmentLinkOriginId === bookAppointmentLinkId) {
        this._stepObjectiveStore.setMessageField(
          originalValues.data_collection_instruction?.value || ''
        )
      } else {
        if (!isShareLink) {
          this._setDefaultInstructions()
        } else {
          this._stepObjectiveStore.setMessageField('')
          setDataCollectionInstruction('')
        }
      }
    }

    this._loading = false
  }
}
